import React, { useCallback, useEffect, useState } from 'react'
import { Button, Card, CardContent, CircularProgress, Grid, Typography } from '@material-ui/core'
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles'

import { connectAdminThemeV2 } from '../../../../connectTheme'
import palette from '../../../../themes/palette'
import EmailTestBenchApi, {
  ConversationOption,
  Mailer,
  MailerCatalogue as Catalogue,
  RunResult as Result,
  TopicOption
} from '../../../../api/EmailTestBenchApi'
import AdminMetaTags from '../../../shared/AdminMetaTags'
import MailerCatalogue from './MailerCatalogue'
import AboutEmail from './AboutEmail'
import ContextPanel from './ContextPanel'
import RunResultPanel from './RunResult'
import {
  ACTION_PREVIEW,
  ACTION_PREVIEW_SUB,
  ACTION_SEND,
  ACTION_SEND_SUB,
  FORBIDDEN_BODY,
  FORBIDDEN_TITLE,
  IDLE_BODY,
  IDLE_TITLE,
  NETWORK_BODY,
  NETWORK_TITLE,
  PAGE_SUBTITLE,
  PAGE_TITLE,
  RESOLVES_TO,
  SENDS_TO_YOU
} from './copy'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    section: { marginBottom: theme.spacing(2) },
    topbar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: theme.spacing(2)
    },
    recipient: { fontFamily: 'monospace' },
    pane: { padding: '0 !important' },
    detail: { padding: theme.spacing(2, 2.5) },
    slug: {
      fontFamily: 'monospace',
      fontSize: 12,
      color: palette.lightergrey,
      marginBottom: theme.spacing(2)
    },
    slugValue: { color: palette.teal, fontWeight: 600 },
    actions: { display: 'flex', gap: theme.spacing(1.5), flexWrap: 'wrap', marginBottom: theme.spacing(2) },
    // Stacked label so the recipient sits ON the send button, not merely near
    // it. Stating it twice is the safety property, not a flourish.
    buttonStack: { display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.25 },
    buttonSub: { fontWeight: 400, fontSize: 11, opacity: 0.85 },
    idle: { padding: theme.spacing(4, 2), textAlign: 'center' },
    loading: { padding: theme.spacing(6), textAlign: 'center' }
  })
)

type LoadState = 'loading' | 'ready' | 'forbidden' | 'error'

/**
 * Admin → Dev → Email Test Bench.
 *
 * A trigger, not a test harness. A super admin picks an email, picks context if
 * that email needs any, and either renders it here or sends it to their own
 * inbox. THE HUMAN IS THE VERIFIER -- there is no pass/fail anywhere on this
 * page, deliberately.
 *
 * 🔴 THERE IS NO RECIPIENT CONTROL ON THIS PAGE AND THERE MUST NEVER BE ONE.
 * A send goes to `current_user.email`, derived on the server and validated
 * against RecipientAllowList; the address shown below arrives FROM the server
 * and is never posted back to it. A field here would not be honoured -- the API
 * has no parameter to receive it -- but it would advertise a capability the
 * design exists to withhold, and somebody would eventually build the parameter
 * to match the field.
 */
const EmailTestBench: React.FC = () => {
  const classes = useStyles()

  const [state, setState] = useState<LoadState>('loading')
  const [catalogue, setCatalogue] = useState<Catalogue | null>(null)
  const [conversations, setConversations] = useState<ConversationOption[]>([])
  const [topics, setTopics] = useState<TopicOption[]>([])
  const [topicsLoaded, setTopicsLoaded] = useState(false)

  const [filter, setFilter] = useState('')
  const [selected, setSelected] = useState<Mailer | null>(null)
  const [conversationId, setConversationId] = useState<number | undefined>(undefined)
  const [topicId, setTopicId] = useState<number | undefined>(undefined)

  const [running, setRunning] = useState(false)
  const [result, setResult] = useState<Result | null>(null)

  useEffect(() => {
    let cancelled = false

    EmailTestBenchApi.getMailers()
      .then((data) => {
        if (cancelled) return
        // A 401 comes back as `{ error: 'unauthorized' }` with a 200-shaped
        // body from api/utils, so the absence of `groups` is what identifies
        // it. Checking the shape rather than the status keeps this honest
        // whichever way the helper evolves.
        if (!data || !Array.isArray(data.groups)) {
          setState('forbidden')
          return
        }
        setCatalogue(data)
        setState('ready')
      })
      .catch(() => {
        if (!cancelled) setState('error')
      })

    EmailTestBenchApi.getConversations()
      .then((data) => {
        if (cancelled) return
        setConversations(Array.isArray(data?.conversations) ? data.conversations : [])
      })
      .catch(() => undefined)

    return () => {
      cancelled = true
    }
  }, [])

  // Topics are fetched only when a topic-shaped mailer is first selected --
  // two of the 48 need them, so loading the list on mount would be a request
  // nobody asked for on every page open.
  useEffect(() => {
    if (selected?.context_kind !== 'topic' || topicsLoaded) return

    setTopicsLoaded(true)
    EmailTestBenchApi.getTopics()
      .then((data) => setTopics(Array.isArray(data?.topics) ? data.topics : []))
      .catch(() => undefined)
  }, [selected, topicsLoaded])

  const select = useCallback((mailer: Mailer) => {
    setSelected(mailer)
    // A result belongs to the mailer that produced it. Leaving it on screen
    // across a selection change would show one email's preview under another
    // email's name.
    setResult(null)
  }, [])

  // The records the page is DISPLAYING, resolved once and used for both the
  // sentence in the context panel and the request that follows.
  //
  // Not left to the server's own default. The server does have one -- and it
  // agrees with this today -- but "what the panel says" and "what was rendered"
  // would then be two implementations that have to keep agreeing forever, and
  // the first time they drift the page will confidently name a conversation the
  // email was not built from.
  const shownConversation =
    conversations.find((c) => c.id === conversationId) ||
    conversations.find((c) => c.default) ||
    conversations[0]
  const shownTopic = topics.find((t) => t.id === topicId) || topics[0]

  const run = useCallback(
    (mode: 'preview' | 'send') => {
      if (!selected) return

      const args: { mailer: string; conversation_id?: number; topic_id?: number } = {
        mailer: selected.key
      }
      if (selected.context_kind === 'topic') {
        if (shownTopic) args.topic_id = shownTopic.id
      } else if (selected.context_kind !== 'none' && shownConversation) {
        // "none" sends nothing: those seven mailers need no record the user
        // picked, the page shows no picker for them, and naming a conversation
        // in the request would imply otherwise.
        args.conversation_id = shownConversation.id
      }
      // NOTE: no recipient. See the header comment. The server derives it.

      setRunning(true)
      setResult(null)
      const request = mode === 'preview' ? EmailTestBenchApi.preview(args) : EmailTestBenchApi.send(args)

      request
        .then((data) => setResult(data))
        .catch(() => setResult({ status: 'failed', reasons: [NETWORK_BODY] }))
        .finally(() => setRunning(false))
    },
    [selected, shownConversation, shownTopic]
  )

  if (state === 'loading') {
    return (
      <div className={classes.loading}>
        <CircularProgress />
      </div>
    )
  }

  if (state !== 'ready' || !catalogue) {
    const forbidden = state === 'forbidden'
    return (
      <Card>
        <CardContent>
          <Typography variant='h6' gutterBottom>
            {forbidden ? FORBIDDEN_TITLE : NETWORK_TITLE}
          </Typography>
          <Typography variant='body2' color='textSecondary'>
            {forbidden ? FORBIDDEN_BODY : NETWORK_BODY}
          </Typography>
        </CardContent>
      </Card>
    )
  }

  const slug = result?.message?.template_slug

  return (
    <>
      <AdminMetaTags title={PAGE_TITLE} />

      <Typography variant='h5' gutterBottom>
        {PAGE_TITLE}
      </Typography>
      <Typography variant='body2' color='textSecondary' paragraph>
        {PAGE_SUBTITLE}
      </Typography>

      {/* Stated up front, not buried. It IS the safety property, so it is made
          visible rather than left implicit -- and it is said again on the send
          button below. */}
      <Card className={classes.section}>
        <CardContent className={classes.topbar}>
          <Typography variant='body2' id='ic-bench-recipient'>
            {SENDS_TO_YOU} <strong className={classes.recipient}>{catalogue.recipient}</strong>
          </Typography>
          <Typography variant='caption' color='textSecondary'>
            {catalogue.available_count} of {catalogue.total_count} available · {catalogue.environment}
          </Typography>
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent className={classes.pane}>
              <MailerCatalogue
                groups={catalogue.groups}
                availableCount={catalogue.available_count}
                filter={filter}
                onFilterChange={setFilter}
                selectedKey={selected?.key}
                onSelect={select}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card>
            <CardContent className={classes.detail}>
              {!selected ? (
                <div className={classes.idle}>
                  <Typography variant='h6' gutterBottom>
                    {IDLE_TITLE}
                  </Typography>
                  <Typography variant='body2' color='textSecondary'>
                    {IDLE_BODY}
                  </Typography>
                </div>
              ) : (
                <>
                  <Typography variant='h6'>{selected.label}</Typography>
                  <div className={classes.slug} id='ic-bench-detail-key'>
                    {selected.key}
                    {slug && (
                      <>
                        {` · ${RESOLVES_TO} `}
                        <span className={classes.slugValue}>{slug}</span>
                      </>
                    )}
                  </div>

                  {/* Above the context picker on purpose: you learn what the
                      thing IS before you configure it and fire it. Rendered
                      unconditionally -- AboutEmail returns null when the mailer
                      has nothing to say, which is most of the 48. Its own
                      docblock carries the rest of the reasoning, including why
                      the locked reason stays in ContextPanel. */}
                  <AboutEmail mailer={selected} />

                  <ContextPanel
                    mailer={selected}
                    conversations={conversations}
                    topics={topics}
                    loading={selected.context_kind === 'topic' ? !topicsLoaded : false}
                    conversationId={shownConversation?.id}
                    topicId={shownTopic?.id}
                    onConversationChange={setConversationId}
                    onTopicChange={setTopicId}
                  />

                  <div className={classes.actions}>
                    <div>
                      <Button
                        variant='outlined'
                        id='ic-bench-preview'
                        disabled={!selected.runnable || running}
                        onClick={() => run('preview')}
                      >
                        <span className={classes.buttonStack}>
                          {ACTION_PREVIEW}
                          <span className={classes.buttonSub}>{ACTION_PREVIEW_SUB}</span>
                        </span>
                      </Button>
                    </div>
                    <div>
                      <Button
                        variant='contained'
                        color='primary'
                        id='ic-bench-send'
                        disabled={!selected.runnable || running}
                        onClick={() => run('send')}
                      >
                        <span className={classes.buttonStack}>
                          {ACTION_SEND}
                          <span className={classes.buttonSub}>
                            {ACTION_SEND_SUB(catalogue.recipient)}
                          </span>
                        </span>
                      </Button>
                    </div>
                  </div>

                  {running && <CircularProgress size={24} />}
                  {result && <RunResultPanel result={result} />}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  )
}

export default connectAdminThemeV2(EmailTestBench)
